// v10/src/components/admin/AdminFlightLogImport.js
// PR #6 (Vision) — Importar planillas históricas con Claude Vision.
//
// Flujo:
//   1) subir N fotos (hasta 10) → cada una comprimida en cliente antes del scan
//   2) /api/vision/scan-flight-log procesa cada foto (concurrency 3, allSettled)
//   3) tabla editable por planilla, sidebar con las planillas del lote
//   4) admin revisa cada planilla, corrige, vincula piloto/instructor si quiere,
//      y confirma UNA por vez (POST por planilla). Sin todo-o-nada.
//   5) borrador persistido en localStorage por si se cierra la pestaña.
//
// Reglas del proyecto:
//   - NO toca completeHobbs ⇒ los vuelos importados no debitan paquetes/services
//     ni descuentan stock ni actualizan hobbs_current del avión.
//   - Vinculación de piloto/instructor es OPCIONAL. Si NO se vincula, el nombre
//     manuscrito se guarda en pilot_name_text / instructor_name (texto libre).
//   - Reversión: borrar el lote desde "Lotes importados" elimina los vuelos
//     por cascada (ON DELETE CASCADE en hobbs_records.import_id).
//   - Multi: cada planilla se guarda como su propio lote (mismo endpoint que
//     el flujo single). El lote puede mezclar aeronaves y meses distintos.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Upload, Camera, Images, Loader, Trash2, Check, AlertTriangle, ScanLine, History,
  RotateCcw, Pencil, X, Plus, RefreshCw, FileSpreadsheet, Download, Wrench, Info,
} from 'lucide-react';
import { aircraftService, userService, flightLogImportService } from '../../services/apiService';
import {
  parseSpreadsheetFile, downloadTemplate, isSpreadsheetFile,
} from '../../utils/flightLogSpreadsheet';

const PURPOSE_OPTIONS = [
  { value: '',         label: '—' },
  { value: 'ENT',      label: 'Entrenamiento (ENT)' },
  { value: 'VP',       label: 'Vuelo personal (VP)' },
  { value: 'ADAP',     label: 'Adaptación (ADAP)' },
  { value: 'READAP',   label: 'Readaptación (READAP)' },
  { value: 'INST',     label: 'Instrucción (INST)' },
  { value: 'Bautismo', label: 'Bautismo' },
  { value: 'Excursion',label: 'Excursión' },
  { value: 'Otros',    label: 'Otros' },
];
const PAYMENT_OPTIONS = [
  { value: '',                 label: '—' },
  { value: 'Transferencia',    label: 'Transferencia' },
  { value: 'Cuenta corriente', label: 'Cuenta corriente' },
  { value: 'Pagado',           label: 'Pagado' },
  { value: 'Paquete',          label: 'Paquete' },
  { value: 'Efectivo',         label: 'Efectivo' },
];

const MAX_PLANILLAS   = 10;
// Techo separado para archivos Excel/CSV. El de fotos queda en 10 (usabilidad
// de foto por foto). Los archivos pueden generar decenas de planillas por
// auto-agrupación (aeronave × mes). 50 cubre casos típicos de digitalización
// histórica gradual (ej. 5 aeronaves × 10 meses o 2 aeronaves × 24 meses) sin
// hacer explotar el borrador de localStorage. Casos con más volumen se
// dividen en tandas por año o por aeronave.
const MAX_PLANILLAS_SPREADSHEET = 50;
const SCAN_CONCURRENCY = 3;
const DRAFT_KEY        = 'condor360_flight_log_import_draft_v1';

// "1:30" → 1.5, "1.5" → 1.5
const parseFlightTime = (v) => {
  if (v == null) return null;
  const s = String(v).trim();
  if (!s) return null;
  if (/^\d+(\.\d+)?$/.test(s)) return parseFloat(s);
  const m = s.match(/^(\d+):(\d{1,2})$/);
  if (m) return parseInt(m[1], 10) + parseInt(m[2], 10) / 60;
  const n = parseFloat(s);
  return isNaN(n) ? null : n;
};

const getCellValue = (cell) => {
  if (cell == null) return '';
  if (typeof cell === 'object' && 'value' in cell) return cell.value == null ? '' : String(cell.value);
  return String(cell);
};
const getCellConfidence = (cell) =>
  (cell && typeof cell === 'object' && cell.confidence) ? cell.confidence : 'high';

// Normaliza una fila parseada por Vision a la estructura de edición local
const visionRowToEditable = (r) => ({
  id:               null,                  // import nuevo ⇒ no hay id todavía
  day:              getCellValue(r.day),
  pilot:            getCellValue(r.pilot),
  purpose:          getCellValue(r.purpose),
  purpose_other:    '',
  dep_time:         getCellValue(r.dep_time),
  from:             getCellValue(r.from),
  to:               getCellValue(r.to),
  arr_time:         getCellValue(r.arr_time),
  flight_time:      getCellValue(r.flight_time),
  landings:         getCellValue(r.landings),
  oil:              getCellValue(r.oil),
  instructor:       getCellValue(r.instructor),
  hobbs_start:      getCellValue(r.hobbs_start),
  hobbs_end:        getCellValue(r.hobbs_end),
  payment_method:   getCellValue(r.payment_method),
  // Vínculos opcionales (admin elige)
  recorded_by:        '',
  instructor_user_id: '',
  // "notes" libres que NO encajan en dep_time/from/to/arr_time/oil.
  // En el import nuevo siempre arrancan vacíos; en edición se prellenan
  // con lo que sobra del notes original.
  notes_extra:      '',
  // Trazabilidad de confianza por celda
  _low: {
    day:           getCellConfidence(r.day)           === 'low',
    pilot:         getCellConfidence(r.pilot)         === 'low',
    purpose:       getCellConfidence(r.purpose)       === 'low',
    dep_time:      getCellConfidence(r.dep_time)      === 'low',
    from:          getCellConfidence(r.from)          === 'low',
    to:            getCellConfidence(r.to)            === 'low',
    arr_time:      getCellConfidence(r.arr_time)      === 'low',
    flight_time:   getCellConfidence(r.flight_time)   === 'low',
    landings:      getCellConfidence(r.landings)      === 'low',
    oil:           getCellConfidence(r.oil)           === 'low',
    instructor:    getCellConfidence(r.instructor)    === 'low',
    hobbs_start:   getCellConfidence(r.hobbs_start)   === 'low',
    hobbs_end:     getCellConfidence(r.hobbs_end)     === 'low',
    payment_method:getCellConfidence(r.payment_method)=== 'low',
  },
});

const NOTE_PATTERNS = [
  { key: 'dep_time', re: /^Salida (.+)$/i  },
  { key: 'arr_time', re: /^Llegada (.+)$/i },
  { key: 'from',     re: /^Desde (.+)$/i   },
  { key: 'to',       re: /^Hasta (.+)$/i   },
  { key: 'oil',      re: /^Aceite (.+)$/i  },
];
const parseNotesToFields = (notes) => {
  const out = { dep_time: '', arr_time: '', from: '', to: '', oil: '', extra: '' };
  if (!notes) return out;
  const parts = String(notes).split(' · ').map(s => s.trim()).filter(Boolean);
  const extras = [];
  for (const p of parts) {
    let matched = false;
    for (const { key, re } of NOTE_PATTERNS) {
      const m = p.match(re);
      if (m) { out[key] = m[1]; matched = true; break; }
    }
    if (!matched) extras.push(p);
  }
  out.extra = extras.join(' · ');
  return out;
};

const dbRowToEditable = (r) => {
  const parsed = parseNotesToFields(r.notes);
  const pilotVisible = r.recorded_by
    ? (r.pilot_name || '')
    : (r.pilot_name_text || '');
  const instructorVisible = r.instructor_user_id
    ? (r.instructor_db_name || '')
    : (r.instructor_name || '');
  return {
    id:               r.id,
    day:              r.day != null ? String(r.day) : '',
    pilot:            pilotVisible,
    purpose:          r.purpose || '',
    purpose_other:    r.purpose_other || '',
    dep_time:         parsed.dep_time,
    from:             parsed.from,
    to:               parsed.to,
    arr_time:         parsed.arr_time,
    flight_time:      r.flight_time_manual != null ? String(r.flight_time_manual) : '',
    landings:         r.landings != null ? String(r.landings) : '',
    oil:              parsed.oil,
    instructor:       instructorVisible,
    hobbs_start:      r.hobbs_start != null ? String(r.hobbs_start) : '',
    hobbs_end:        r.hobbs_end   != null ? String(r.hobbs_end)   : '',
    payment_method:   r.payment_method || '',
    recorded_by:        r.recorded_by || '',
    instructor_user_id: r.instructor_user_id || '',
    notes_extra:      parsed.extra,
    _low: {},
  };
};

// ── Compresión y thumbnail ─────────────────────────────────────────
// Devuelve { dataUrl, base64 } listo para /vision/scan-flight-log.
// maxDim=2000 + q=0.85 es un compromiso conservador: bajan drásticamente
// los MB de fotos de celular sin degradar la legibilidad para Vision.
const compressImage = (file, maxDim = 2000, quality = 0.85) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = (ev) => {
    const img = new Image();
    img.onload = () => {
      let { width, height } = img;
      if (width > maxDim || height > maxDim) {
        if (width >= height) {
          height = Math.round(height * (maxDim / width));
          width  = maxDim;
        } else {
          width  = Math.round(width * (maxDim / height));
          height = maxDim;
        }
      }
      const canvas = document.createElement('canvas');
      canvas.width  = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);
      try {
        const dataUrl = canvas.toDataURL('image/jpeg', quality);
        resolve({ dataUrl, base64: dataUrl.split(',')[1], mediaType: 'image/jpeg' });
      } catch (err) {
        reject(err);
      }
    };
    img.onerror = () => reject(new Error('No se pudo leer la imagen'));
    img.src = ev.target.result;
  };
  reader.onerror = () => reject(new Error('No se pudo leer el archivo'));
  reader.readAsDataURL(file);
});

// ── Concurrency limit casero (sin lib extra) ────────────────────────
// Corre `worker(item, idx)` sobre `items` con hasta `limit` en vuelo.
// No aborta el batch si una tarea rechaza; el worker debe capturar su error.
const runWithConcurrency = async (items, limit, worker) => {
  let cursor = 0;
  const spawn = async () => {
    while (true) {
      const i = cursor++;
      if (i >= items.length) return;
      await worker(items[i], i);
    }
  };
  const runners = Array.from({ length: Math.min(limit, items.length) }, spawn);
  await Promise.all(runners);
};

// ── UUID local para planillas ───────────────────────────────────────
const genPlanillaId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `p_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
};

// ── Chequeo de baja confianza y validación por planilla ──────────────
const hasLowConfidence = (planilla) =>
  planilla.rows.some(r => r._low && Object.values(r._low).some(Boolean));

const validatePlanilla = (p) => {
  if (!p.aircraftId)   return 'Seleccioná la aeronave de la planilla';
  if (!p.year || !p.month) return 'Indicá año y mes de la planilla';
  if (parseInt(p.month, 10) < 1 || parseInt(p.month, 10) > 12) return 'Mes inválido (1-12)';
  if (!p.rows.length)  return 'No hay filas para importar';
  for (let i = 0; i < p.rows.length; i++) {
    const r = p.rows[i];
    const day = parseInt(r.day, 10);
    if (!day || day < 1 || day > 31) return `Fila ${i + 1}: día inválido`;
    if (r.purpose === 'Otros' && !r.purpose_other.trim()) {
      return `Fila ${i + 1}: indicá el detalle de "Otros"`;
    }
    const hs = parseFloat(r.hobbs_start);
    const he = parseFloat(r.hobbs_end);
    if (!isNaN(hs) && !isNaN(he) && he < hs) {
      return `Fila ${i + 1}: horómetro fin menor al inicio`;
    }
  }
  return null;
};

// Payload común usado por POST (import) y PUT (update). Los `notes_extra`
// solo se re-anexan cuando editamos un lote existente (edit mode).
const buildRowPayload = (r, { includeId, keepExtra }) => ({
  ...(includeId ? { id: r.id || null } : {}),
  day:                parseInt(r.day, 10),
  pilot_name_text:    r.recorded_by ? null : (r.pilot || null),
  recorded_by:        r.recorded_by || null,
  purpose:            r.purpose || null,
  purpose_other:      r.purpose === 'Otros' ? r.purpose_other : null,
  payment_method:     r.payment_method || null,
  landings:           r.landings === '' ? null : parseInt(r.landings, 10),
  hobbs_start:        r.hobbs_start === '' ? null : parseFloat(r.hobbs_start),
  hobbs_end:          r.hobbs_end   === '' ? null : parseFloat(r.hobbs_end),
  flight_time:        r.flight_time || null,
  instructor_user_id: r.instructor_user_id || null,
  instructor_name:    r.instructor_user_id ? null : (r.instructor || null),
  notes: [
    r.dep_time && `Salida ${r.dep_time}`,
    r.arr_time && `Llegada ${r.arr_time}`,
    r.from     && `Desde ${r.from}`,
    r.to       && `Hasta ${r.to}`,
    r.oil      && `Aceite ${r.oil}`,
    keepExtra ? (r.notes_extra || null) : null,
  ].filter(Boolean).join(' · ') || null,
});

const buildImportPayload = (p) => ({
  aircraft_id:   p.aircraftId,
  year:          parseInt(p.year, 10),
  month:         parseInt(p.month, 10),
  period_label:  `${p.year}-${String(p.month).padStart(2, '0')}`,
  note:          p.note || null,
  rows: p.rows.map(r => buildRowPayload(r, { includeId: false, keepExtra: false })),
});

// ── Duplicados ──────────────────────────────────────────────────────
// La clave de duplicado es aeronave + período (year-month), en el mismo
// formato que `period_label` de `flight_log_imports` (YYYY-MM). Devuelve
// null si falta info o el mes/año no es válido.
const getDupPeriodLabel = (year, month) => {
  const y = String(year || '').trim();
  const mNum = parseInt(month, 10);
  if (!/^\d{4}$/.test(y) || !mNum || mNum < 1 || mNum > 12) return null;
  return `${y}-${String(mNum).padStart(2, '0')}`;
};

const getDupKey = (aircraftId, year, month) => {
  if (!aircraftId) return null;
  const period = getDupPeriodLabel(year, month);
  return period ? `${aircraftId}::${period}` : null;
};

// ── Persistencia del borrador ───────────────────────────────────────
// Guarda todo menos el File original (que ya no hace falta post-scan) y
// mantiene una miniatura chica para que el sidebar/banner tenga preview.
const serializePlanilla = (p) => ({
  id:               p.id,
  thumbnail:        p.thumbnail || null,
  aircraftId:       p.aircraftId,
  year:             p.year,
  month:            p.month,
  registrationText: p.registrationText,
  note:             p.note,
  rows:             p.rows,
  status:           p.status === 'confirming' ? 'scanned' : p.status,
  error:            p.error || null,
  importId:         p.importId || null,
  createdAt:        p.createdAt,
  source:           p.source || null,
});

const loadDraft = () => {
  try {
    const raw = window.localStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.planillas)) return null;
    return parsed;
  } catch (_) { return null; }
};

const saveDraft = (planillas) => {
  try {
    // Solo persistimos las que NO fueron confirmadas (esas ya están en DB)
    // ni las que están escaneando (necesitan el File para reintentar).
    const draftable = planillas
      .filter(p => p.status !== 'confirmed' && p.status !== 'scanning')
      .map(serializePlanilla);
    if (!draftable.length) {
      window.localStorage.removeItem(DRAFT_KEY);
      return;
    }
    window.localStorage.setItem(DRAFT_KEY, JSON.stringify({
      savedAt: Date.now(),
      planillas: draftable,
    }));
  } catch (_) { /* quota o SSR — silencioso */ }
};

const clearDraft = () => {
  try { window.localStorage.removeItem(DRAFT_KEY); } catch (_) {}
};

const useIsDesktopReview = () => {
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== 'undefined' && window.innerWidth >= 1280
  );
  useEffect(() => {
    const handler = () => setIsDesktop(window.innerWidth >= 1280);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isDesktop;
};

// ── Badge por planilla (visual) ─────────────────────────────────────
const statusBadge = (p) => {
  if (p.status === 'confirmed')  return { key: 'confirmed',  label: 'Confirmada',    color: '#16a34a', bg: 'rgba(22,163,74,0.15)',  icon: '✓' };
  if (p.status === 'confirming') return { key: 'confirming', label: 'Guardando…',    color: '#7c3aed', bg: 'rgba(124,58,237,0.15)', icon: '⏳' };
  if (p.status === 'failed')     return { key: 'failed',     label: 'Falla scan',    color: '#dc2626', bg: 'rgba(239,68,68,0.15)',  icon: '!' };
  if (p.status === 'scanning')   return { key: 'scanning',   label: 'Procesando',    color: '#7c3aed', bg: 'rgba(124,58,237,0.15)', icon: '…' };
  if (hasLowConfidence(p))       return { key: 'review',     label: 'Revisar',       color: '#d97706', bg: 'rgba(245,158,11,0.15)', icon: '⚠' };
  return                                { key: 'ready',      label: 'Lista',         color: '#0891b2', bg: 'rgba(6,182,212,0.15)',  icon: '●' };
};

// Ranking para ordenar el sidebar: dudosas primero, confirmadas al final.
const badgeRank = (p) => {
  const b = statusBadge(p).key;
  return { review: 0, failed: 1, ready: 2, scanning: 3, confirming: 3, confirmed: 4 }[b] ?? 5;
};

const AdminFlightLogImport = ({ darkMode }) => {
  const isDesktopReview = useIsDesktopReview();

  // ── Estado general ───────────────────────────────────────────────
  const [stage, setStage] = useState('idle'); // idle | scanning | review | importing | done
  const [error, setError] = useState('');

  // Multi-foto: colas de archivos pendientes (aún sin scan) y las planillas
  // ya escaneadas (o en scan). Cada pending file tiene su miniatura para preview.
  const [pendingFiles, setPendingFiles] = useState([]); // [{ id, file, thumbnail }]
  const [planillas, setPlanillas]       = useState([]);  // [Planilla]
  const [selectedIdx, setSelectedIdx]   = useState(0);
  // fileInputRef = galería (multi, sin capture). cameraInputRef = cámara
  // (capture=environment, single). Ambos disparan el mismo handler para
  // acumular en el mismo lote respetando el límite MAX_PLANILLAS.
  // spreadsheetInputRef = Excel/CSV (single, sin capture). Handler propio
  // porque no comparte flujo con las fotos (parseo sincrónico en cliente).
  const fileInputRef        = useRef(null);
  const cameraInputRef      = useRef(null);
  const spreadsheetInputRef = useRef(null);
  // Loading del parseo (útil para archivos grandes donde XLSX.read tarda
  // unos ms; también gatea el botón para evitar doble-click).
  const [parsingSpreadsheet, setParsingSpreadsheet] = useState(false);

  // Catálogos
  const [aircraftList, setAircraftList] = useState([]);
  const [usersList, setUsersList]       = useState([]);

  // Lotes importados
  const [batches, setBatches]           = useState([]);
  const [loadingBatches, setLoadingBatches] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deletingId, setDeletingId]     = useState(null);

  // Modo edición de un lote ya importado (single-planilla, sin sidebar).
  const [editMode, setEditMode]       = useState(false);
  const [editLotId, setEditLotId]     = useState(null);
  const [loadingEdit, setLoadingEdit] = useState(false);

  // Borrador detectado en localStorage al montar
  const [draftAvailable, setDraftAvailable] = useState(null); // { savedAt, planillas } | null

  // Modal para confirmar "Empezar de nuevo" cuando hay planillas en juego
  const [confirmStartOver, setConfirmStartOver] = useState(false);

  // Aviso de posibles duplicados (aeronave + año-mes) al confirmar una planilla.
  // Shape: { mode: 'single', idx, existingBatches, intraLotPlanillas }
  const [duplicateWarning, setDuplicateWarning] = useState(null);

  // Aviso post-import: se acumula al confirmar planillas exitosamente. Recuerda
  // al admin que el mantenimiento (`hours_remaining` de los componentes de
  // service) NO se ajusta automáticamente, para no dispararse AOG falsos ni
  // enmascarar services vencidos. El admin decide desde el tab Horómetro.
  // Shape: { [aircraftId]: { aircraftId, label, count } } — un slot por aeronave.
  const [postImportNotices, setPostImportNotices] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const acRes = await aircraftService.getAll({ is_active: true });
        const arr = acRes.data?.data || acRes.data || [];
        setAircraftList(Array.isArray(arr) ? arr : []);
      } catch (_) { setAircraftList([]); }
      try {
        const usRes = await userService.getAll();
        const arr = usRes.data?.data || usRes.data || [];
        setUsersList(Array.isArray(arr) ? arr : []);
      } catch (_) { setUsersList([]); }
    })();
    loadBatches();
    // Detectar borrador solo una vez
    const draft = loadDraft();
    if (draft && draft.planillas.length) setDraftAvailable(draft);
  }, []);

  // Persistencia: cada vez que cambian las planillas, guardar draft.
  // - En edit mode NO persistimos (es otro flujo).
  // - Si hay un draft esperando decisión del usuario (banner Continuar/Descartar),
  //   NO tocarlo hasta que resuelva; sino lo pisaríamos con [] al montar.
  useEffect(() => {
    if (editMode) return;
    if (draftAvailable) return;
    saveDraft(planillas);
  }, [planillas, editMode, draftAvailable]);

  const loadBatches = async () => {
    setLoadingBatches(true);
    try {
      const res = await flightLogImportService.list();
      setBatches(res.data?.data || []);
    } catch (_) { setBatches([]); }
    finally { setLoadingBatches(false); }
  };

  // ── Autoselección de matrícula sobre la planilla seleccionada ─────
  useEffect(() => {
    if (!planillas.length || !aircraftList.length) return;
    const p = planillas[selectedIdx];
    if (!p || !p.registrationText || p.aircraftId) return;
    const norm = (s) => String(s || '').replace(/[\s-]/g, '').toUpperCase();
    const target = norm(p.registrationText);
    const match = aircraftList.find(a => norm(a.registration) === target);
    if (match) updatePlanilla(selectedIdx, { aircraftId: match.id });
    // eslint-disable-next-line
  }, [aircraftList, selectedIdx, planillas]);

  // ── Helpers de mutación de estado ────────────────────────────────
  const updatePlanilla = (idx, patch) => {
    setPlanillas(prev => prev.map((p, i) => i === idx ? { ...p, ...patch } : p));
  };

  const updateSelected = (patch) => updatePlanilla(selectedIdx, patch);

  const updateSelectedRow = (rowIdx, patch) => {
    setPlanillas(prev => prev.map((p, i) => {
      if (i !== selectedIdx) return p;
      return { ...p, rows: p.rows.map((r, j) => j === rowIdx ? { ...r, ...patch } : r) };
    }));
  };

  const removeSelectedRow = (rowIdx) => {
    setPlanillas(prev => prev.map((p, i) => {
      if (i !== selectedIdx) return p;
      return { ...p, rows: p.rows.filter((_, j) => j !== rowIdx) };
    }));
  };

  const addEmptyRowSelected = () => {
    setPlanillas(prev => prev.map((p, i) => {
      if (i !== selectedIdx) return p;
      return { ...p, rows: [...p.rows, visionRowToEditable({})] };
    }));
  };

  // ── Upload multi ─────────────────────────────────────────────────
  const handlePickFile        = () => fileInputRef.current?.click();
  const handlePickCamera      = () => cameraInputRef.current?.click();
  const handlePickSpreadsheet = () => spreadsheetInputRef.current?.click();
  const handleDownloadTemplate = () => { try { downloadTemplate(); } catch (_) {} };

  const handleFilesSelected = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    const remaining = MAX_PLANILLAS - pendingFiles.length - planillas.length;
    if (remaining <= 0) {
      setError(`Límite alcanzado: máximo ${MAX_PLANILLAS} planillas por lote.`);
      return;
    }
    let toAdd = files;
    if (files.length > remaining) {
      toAdd = files.slice(0, remaining);
      setError(`Solo se agregaron las primeras ${remaining} fotos (límite ${MAX_PLANILLAS}).`);
    } else {
      setError('');
    }
    // Miniaturas rápidas para preview (comprimidas chicas para bajar peso)
    const prepared = [];
    for (const file of toAdd) {
      try {
        const { dataUrl } = await compressImage(file, 480, 0.7);
        prepared.push({ id: genPlanillaId(), file, thumbnail: dataUrl });
      } catch (_) {
        // Si falla el thumbnail, igual dejamos entrar el archivo sin preview
        prepared.push({ id: genPlanillaId(), file, thumbnail: null });
      }
    }
    // En review, los archivos nuevos entran directo a scanning (no hay preview intermedio).
    // En idle, van a pendingFiles y el usuario decide cuándo escanear.
    if (stage === 'review') {
      await scanFileGroup(prepared);
    } else {
      setPendingFiles(prev => [...prev, ...prepared]);
    }
  };

  const removePendingFile = (id) => {
    setPendingFiles(prev => prev.filter(f => f.id !== id));
  };

  // ── Upload Excel/CSV ─────────────────────────────────────────────
  // Parseo sincrónico en cliente (xlsx / SheetJS). Las planillas resultantes
  // caen directo en la tabla de revisión ⇒ no pasan por estado 'scanning'.
  // El límite es MAX_PLANILLAS_SPREADSHEET (separado del de fotos) y aplica
  // sobre el total de planillas del lote (todas las fuentes suman).
  const handleSpreadsheetSelected = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    const file = files[0];
    if (!isSpreadsheetFile(file)) {
      setError('El archivo no parece un .xlsx, .xls o .csv válido.');
      return;
    }
    const remaining = MAX_PLANILLAS_SPREADSHEET - planillas.length;
    if (remaining <= 0) {
      setError(`Ya alcanzaste el máximo de ${MAX_PLANILLAS_SPREADSHEET} planillas por lote. Confirmá o descartá alguna antes de subir otra.`);
      return;
    }
    setError('');
    setParsingSpreadsheet(true);
    try {
      const result = await parseSpreadsheetFile(file, {
        aircraftList,
        maxPlanillas: remaining,
      });
      if (result.error) {
        setError(result.error);
        return;
      }
      const startIdx = planillas.length;
      const newPlanillas = result.planillas.map(p => ({
        ...p,
        id: genPlanillaId(),
        createdAt: Date.now(),
      }));
      setPlanillas(prev => [...prev, ...newPlanillas]);
      // Si es la primera carga desde idle, entramos a review. Si ya estamos
      // en review (agregar desde el sidebar), no cambiamos stage.
      setStage(s => (s === 'idle' ? 'review' : s));
      // Selección: apuntar a la primera de las recién agregadas.
      setSelectedIdx(startIdx);
      if (result.warning) {
        // Warning no bloqueante: mostrar en el mismo banner (rojo por el
        // componente actual, pero el texto indica que se importó lo demás).
        setError(result.warning);
      }
    } catch (err) {
      setError(err?.message || 'Error al procesar el archivo.');
    } finally {
      setParsingSpreadsheet(false);
    }
  };

  // ── Batch scan ───────────────────────────────────────────────────
  const scanOne = async (file) => {
    const { base64, dataUrl, mediaType } = await compressImage(file);
    const res = await flightLogImportService.scan(base64, mediaType);
    const header = res.data?.header || {};
    const visionRows = Array.isArray(res.data?.rows) ? res.data.rows : [];
    return {
      thumbnail:        dataUrl,
      aircraftId:       '',
      year:             header.year != null ? String(header.year) : '',
      month:            header.month != null ? String(header.month) : '',
      registrationText: header.registration || '',
      rows:             visionRows.map(visionRowToEditable),
    };
  };

  // Empuja el grupo a planillas en estado 'scanning' y dispara scans en paralelo.
  // No cambia stage — el caller decide (start batch: idle → scanning → review;
  // agregar en review: se queda en review).
  const scanFileGroup = async (fileGroup) => {
    if (!fileGroup.length) return;
    const initial = fileGroup.map(pf => ({
      id:               pf.id,
      file:             pf.file,
      thumbnail:        pf.thumbnail,
      aircraftId:       '',
      year:             '',
      month:            '',
      registrationText: '',
      note:             '',
      rows:             [],
      status:           'scanning',
      error:            null,
      importId:         null,
      createdAt:        Date.now(),
    }));
    setPlanillas(prev => [...prev, ...initial]);
    const ids = initial.map(p => p.id);
    const files = initial.map(p => p.file);
    await runWithConcurrency(ids, SCAN_CONCURRENCY, async (id, i) => {
      const file = files[i];
      try {
        const scanned = await scanOne(file);
        setPlanillas(prev => prev.map(p => p.id === id ? { ...p, ...scanned, status: 'scanned', error: null } : p));
      } catch (err) {
        const msg = err?.response?.data?.message || err.message || 'Error al escanear';
        setPlanillas(prev => prev.map(p => p.id === id ? { ...p, status: 'failed', error: msg } : p));
      }
    });
  };

  const handleStartBatchScan = async () => {
    if (!pendingFiles.length) return;
    const group = pendingFiles;
    setPendingFiles([]);
    setStage('scanning');
    setError('');
    await scanFileGroup(group);
    setStage('review');
    // Selector: apuntar a la de menor rank (dudosa primero) tras el batch.
    setPlanillas(prev => {
      if (!prev.length) { setSelectedIdx(0); return prev; }
      let bestIdx = 0;
      let bestRank = 999;
      prev.forEach((p, i) => {
        const r = badgeRank(p);
        if (r < bestRank) { bestRank = r; bestIdx = i; }
      });
      setSelectedIdx(bestIdx);
      return prev;
    });
  };

  const handleRetryScan = async (idx) => {
    const p = planillas[idx];
    if (!p) return;
    if (!p.file) {
      setError('Esta planilla proviene de un borrador guardado; no tenemos la foto original para reintentar. Descartala y volvé a subirla.');
      return;
    }
    const id = p.id;
    const file = p.file;
    setPlanillas(prev => prev.map(pp => pp.id === id ? { ...pp, status: 'scanning', error: null } : pp));
    setError('');
    try {
      const scanned = await scanOne(file);
      setPlanillas(prev => prev.map(pp => pp.id === id ? { ...pp, ...scanned, status: 'scanned', error: null } : pp));
    } catch (err) {
      const msg = err?.response?.data?.message || err.message || 'Error al escanear';
      setPlanillas(prev => prev.map(pp => pp.id === id ? { ...pp, status: 'failed', error: msg } : pp));
    }
  };

  // ── Duplicados: helpers de detección ─────────────────────────────
  // Chequea la planilla `idx` contra:
  //   (a) `batches` — lotes ya importados en la DB (misma aeronave + período).
  //   (b) otras planillas del lote actual con misma aeronave + período,
  //       excluyendo las ya confirmadas (esas viven en `batches`, evita
  //       double-count en el modal).
  const getDuplicateInfo = (idx) => {
    const p = planillas[idx];
    if (!p) return { existingBatches: [], intraLotPlanillas: [] };
    const key = getDupKey(p.aircraftId, p.year, p.month);
    if (!key) return { existingBatches: [], intraLotPlanillas: [] };
    const [aircraftId, period] = key.split('::');
    const existingBatches = batches.filter(
      b => b.aircraft_id === aircraftId && b.period_label === period
    );
    const intraLotPlanillas = planillas
      .map((pp, i) => ({ pp, i }))
      .filter(({ pp, i }) =>
        i !== idx &&
        pp.status !== 'confirmed' &&
        getDupKey(pp.aircraftId, pp.year, pp.month) === key
      )
      .map(({ pp, i }) => ({ idx: i, planilla: pp }));
    return { existingBatches, intraLotPlanillas };
  };

  const hasAnyDuplicate = (idx) => {
    const info = getDuplicateInfo(idx);
    return info.existingBatches.length > 0 || info.intraLotPlanillas.length > 0;
  };

  // ── Confirmación por planilla ────────────────────────────────────
  const handleConfirmPlanilla = async (idx, opts = {}) => {
    const p = planillas[idx];
    if (!p) return;
    const v = validatePlanilla(p);
    if (v) { setError(v); return; }
    // Chequeo de duplicado: avisa (no bloquea). El usuario decide en el modal
    // si sigue igual (skipDupCheck=true en el reintento) o descarta la planilla.
    if (!opts.skipDupCheck) {
      const info = getDuplicateInfo(idx);
      if (info.existingBatches.length > 0 || info.intraLotPlanillas.length > 0) {
        setDuplicateWarning({
          mode: 'single',
          idx,
          existingBatches: info.existingBatches,
          intraLotPlanillas: info.intraLotPlanillas,
        });
        return;
      }
    }
    setError('');
    updatePlanilla(idx, { status: 'confirming' });
    try {
      const payload = buildImportPayload(p);
      const res = await flightLogImportService.import(payload);
      const importId = res.data?.data?.import_id || res.data?.import_id || null;
      updatePlanilla(idx, { status: 'confirmed', importId, file: null });
      // Aviso post-import: acumula por aeronave importada en este ciclo.
      // Cerrarlo lo saca del state; se re-emite en cada nueva confirmación.
      if (p.aircraftId) {
        const rows = Array.isArray(p.rows) ? p.rows.length : 0;
        setPostImportNotices((prev) => {
          const existing = prev[p.aircraftId];
          return {
            ...prev,
            [p.aircraftId]: {
              aircraftId: p.aircraftId,
              label:      aircraftLabel(p.aircraftId),
              count:      (existing?.count || 0) + rows,
            },
          };
        });
      }
      await loadBatches();
    } catch (err) {
      const msg = err?.response?.data?.message || err.message || 'Error al importar';
      updatePlanilla(idx, { status: 'scanned' });
      setError(`Planilla ${idx + 1}: ${msg}`);
    }
  };

  const handleDiscardPlanilla = (idx) => {
    setPlanillas(prev => {
      const next = prev.filter((_, i) => i !== idx);
      // Reajustar selectedIdx
      if (!next.length) {
        setSelectedIdx(0);
        // Si no queda nada por hacer, volver al idle
        setStage(s => (s === 'review' ? 'idle' : s));
      } else {
        setSelectedIdx(prevSel => {
          if (prevSel > idx) return prevSel - 1;
          if (prevSel >= next.length) return next.length - 1;
          return prevSel;
        });
      }
      return next;
    });
  };

  // ── Borrador: continuar / descartar ──────────────────────────────
  const handleContinueDraft = () => {
    if (!draftAvailable) return;
    const restored = draftAvailable.planillas.map(dp => ({
      id:               dp.id || genPlanillaId(),
      file:             null,                                          // el file no persiste
      thumbnail:        dp.thumbnail || null,
      aircraftId:       dp.aircraftId || '',
      year:             dp.year || '',
      month:            dp.month || '',
      registrationText: dp.registrationText || '',
      note:             dp.note || '',
      rows:             Array.isArray(dp.rows) ? dp.rows : [],
      status:           dp.status || 'scanned',
      error:            dp.error || null,
      importId:         dp.importId || null,
      createdAt:        dp.createdAt || Date.now(),
      source:           dp.source || null,
    }));
    setPlanillas(restored);
    setSelectedIdx(0);
    setStage('review');
    setDraftAvailable(null);
    setError('');
  };

  const handleDiscardDraft = () => {
    clearDraft();
    setDraftAvailable(null);
  };

  // ── Start over ───────────────────────────────────────────────────
  const performStartOver = () => {
    setStage('idle');
    setError('');
    setPendingFiles([]);
    setPlanillas([]);
    setSelectedIdx(0);
    setEditMode(false);
    setEditLotId(null);
    setConfirmStartOver(false);
    clearDraft();
  };

  const handleStartOver = () => {
    const somethingToLose = planillas.some(p => p.status !== 'confirmed');
    if (somethingToLose || pendingFiles.length) {
      setConfirmStartOver(true);
    } else {
      performStartOver();
    }
  };

  // ── Edit lote existente (single-planilla wrapper) ────────────────
  const handleEditBatch = async (batch) => {
    setError('');
    setLoadingEdit(true);
    try {
      const res = await flightLogImportService.getRows(batch.id);
      const data = res.data?.data || {};
      const p = {
        id:               genPlanillaId(),
        file:             null,
        thumbnail:        null,
        aircraftId:       data.aircraft_id || '',
        year:             data.year ? String(data.year) : '',
        month:            data.month ? String(data.month) : '',
        registrationText: data.registration || '',
        note:             data.note || '',
        rows:             (data.rows || []).map(dbRowToEditable),
        status:           'scanned',
        error:            null,
        importId:         batch.id,
        createdAt:        Date.now(),
      };
      setEditMode(true);
      setEditLotId(batch.id);
      setPlanillas([p]);
      setSelectedIdx(0);
      setStage('review');
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Error al cargar el lote');
    } finally {
      setLoadingEdit(false);
    }
  };

  const handleSaveEdit = async () => {
    const p = planillas[0];
    if (!p) return;
    const v = validatePlanilla(p);
    if (v) { setError(v); return; }
    setStage('importing');
    setError('');
    try {
      const payload = {
        year:  parseInt(p.year, 10),
        month: parseInt(p.month, 10),
        note:  p.note || null,
        rows:  p.rows.map(r => buildRowPayload(r, { includeId: true, keepExtra: true })),
      };
      await flightLogImportService.update(editLotId, payload);
      setStage('done');
      await loadBatches();
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Error al guardar cambios');
      setStage('review');
    }
  };

  // ── Reversión de lote (unchanged) ────────────────────────────────
  const handleDeleteBatch = async (batch) => {
    setDeletingId(batch.id);
    try {
      await flightLogImportService.delete(batch.id);
      await loadBatches();
      setConfirmDelete(null);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Error al revertir el lote');
    } finally { setDeletingId(null); }
  };

  // ── Derivadas / resumen ──────────────────────────────────────────
  const selected = planillas[selectedIdx] || null;

  const selectedTotalHours = useMemo(() => {
    if (!selected) return 0;
    return selected.rows.reduce((acc, r) => {
      const hs = parseFloat(r.hobbs_start);
      const he = parseFloat(r.hobbs_end);
      if (!isNaN(hs) && !isNaN(he) && he >= hs) return acc + (he - hs);
      const ft = parseFlightTime(r.flight_time);
      return ft ? acc + ft : acc;
    }, 0);
  }, [selected]);

  const counts = useMemo(() => {
    const c = { confirmed: 0, ready: 0, pendingReview: 0, failed: 0, scanning: 0 };
    for (const p of planillas) {
      if (p.status === 'confirmed')  { c.confirmed++; continue; }
      if (p.status === 'failed')     { c.failed++; continue; }
      if (p.status === 'scanning' || p.status === 'confirming') { c.scanning++; continue; }
      if (hasLowConfidence(p))       { c.pendingReview++; continue; }
      c.ready++;
    }
    return c;
  }, [planillas]);

  const scanProgress = useMemo(() => {
    const total = planillas.length;
    const done  = planillas.filter(p => p.status !== 'scanning').length;
    return { done, total };
  }, [planillas]);

  const orderedIndices = useMemo(() => (
    planillas
      .map((p, i) => ({ p, i }))
      .sort((a, b) => badgeRank(a.p) - badgeRank(b.p) || a.i - b.i)
      .map(({ i }) => i)
  ), [planillas]);

  const aircraftLabel = (aid) => {
    const a = aircraftList.find(x => x.id === aid);
    return a ? (a.registration || `${a.manufacturer || ''} ${a.model_name || ''}`.trim()) : '—';
  };

  const monthLabel = (m) => {
    const n = parseInt(m, 10);
    if (!n || n < 1 || n > 12) return '—';
    return ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'][n - 1];
  };

  // ── Estilos ──────────────────────────────────────────────────────
  const text = darkMode ? 'white' : '#0f172a';
  const sub  = darkMode ? 'rgba(255,255,255,0.45)' : '#64748b';
  const card = {
    borderRadius: 16,
    background:   darkMode ? 'rgba(255,255,255,0.03)' : 'white',
    border:       darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e2e8f0',
    padding: '20px',
  };
  const inp = (low) => ({
    width: '100%', padding: isDesktopReview ? '7px 8px' : '6px 8px', borderRadius: 8,
    border: low
      ? '1.5px solid #f59e0b'
      : (darkMode ? '1px solid rgba(255,255,255,0.12)' : '1px solid #cbd5e1'),
    background: low
      ? (darkMode ? 'rgba(245,158,11,0.08)' : 'rgba(254,243,199,0.6)')
      : (darkMode ? 'rgba(255,255,255,0.05)' : 'white'),
    color: text, fontSize: isDesktopReview ? 13 : 12, fontFamily: 'inherit', outline: 'none',
    boxSizing: 'border-box', minWidth: 0,
  });

  const tdStyle = (mobileMin, desktopMin) => isDesktopReview
    ? { padding: '8px 10px', minWidth: desktopMin, whiteSpace: 'nowrap', verticalAlign: 'top' }
    : { padding: '6px 4px',  minWidth: mobileMin,  whiteSpace: 'nowrap', verticalAlign: 'top' };

  const chip = (bg, color, extra = {}) => ({
    display: 'inline-flex', alignItems: 'center', gap: 4,
    padding: '2px 8px', borderRadius: 999, fontSize: 10, fontWeight: 700,
    background: bg, color, ...extra,
  });

  const showSidebar = !editMode && planillas.length > 0 && stage === 'review';
  const bulkFooterVisible = !editMode && stage === 'review' && planillas.length > 0;

  // ── Render ───────────────────────────────────────────────────────
  return (
    <div style={{ padding: 24, color: text, paddingBottom: bulkFooterVisible ? 100 : 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, gap: 12, flexWrap: 'wrap' }}>
        <div style={{ minWidth: 0 }}>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800 }}>
            {editMode ? 'Editar lote importado' : 'Importar planillas históricas'}
          </h2>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: sub }}>
            {editMode
              ? 'Corregí filas mal leídas por Vision, borrá o agregá filas. Las horas de los pilotos vinculados se recalculan automáticamente al guardar.'
              : 'Digitalizá planillas de vuelo manuscritas con Claude Vision. Podés subir hasta 10 fotos por lote — cada planilla se confirma por separado.'}
          </p>
        </div>
        {(stage !== 'idle' || planillas.length > 0 || pendingFiles.length > 0) && (
          <button onClick={handleStartOver}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 12, border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0', background: 'transparent', color: sub, cursor: 'pointer', fontWeight: 700, fontSize: 12, fontFamily: 'inherit' }}>
            <RotateCcw style={{ width: 14, height: 14 }} /> Empezar de nuevo
          </button>
        )}
      </div>

      {error && (
        <div style={{ ...card, marginBottom: 16, borderColor: 'rgba(239,68,68,0.4)', background: darkMode ? 'rgba(239,68,68,0.08)' : 'rgba(254,226,226,0.6)', display: 'flex', gap: 10, alignItems: 'center' }}>
          <AlertTriangle style={{ width: 18, height: 18, color: '#ef4444', flexShrink: 0 }} />
          <span style={{ fontSize: 13, fontWeight: 600, color: '#dc2626' }}>{error}</span>
        </div>
      )}

      {/* ── Banner: aviso post-import de mantenimiento ────────────── */}
      {/* Se muestra tras cada import exitoso. Recuerda que el estado de
          service NO cambia solo — el admin decide si aplica "Service hecho"
          (recién hecho) o "Ajustar al estado real" (contador custom), o si
          no toca nada (para histórico viejo). Ver brief y ServiceTab.js. */}
      {Object.keys(postImportNotices).length > 0 && (
        <div style={{
          ...card, marginBottom: 16,
          borderColor: 'rgba(245,158,11,0.45)',
          background: darkMode ? 'rgba(245,158,11,0.08)' : 'rgba(254,243,199,0.55)',
        }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
            <div style={{ width: 40, height: 40, borderRadius: 12, background: 'rgba(245,158,11,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Wrench style={{ width: 18, height: 18, color: '#b45309' }} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ margin: 0, fontSize: 13, fontWeight: 800, color: darkMode ? '#fde68a' : '#92400e' }}>
                Revisá el mantenimiento de las aeronaves importadas
              </p>
              <p style={{ margin: '4px 0 8px', fontSize: 12, color: sub, lineHeight: 1.5 }}>
                El estado de mantenimiento (horas restantes de service) <strong>NO se ajustó automáticamente</strong>. Esto evita AOG falsos por horas históricas y también evita enmascarar services realmente vencidos. Revisá cada aeronave y decidí desde <strong>Mantenimiento → Servicios recurrentes</strong>:
              </p>
              <ul style={{ margin: '0 0 8px', paddingLeft: 20, fontSize: 12, color: sub, lineHeight: 1.55 }}>
                <li><strong>Si el service ya se hizo</strong> entre las horas importadas y hoy → tocá <em>"Service hecho"</em> en el componente (modo "Service recién hecho") con el horómetro real del service.</li>
                <li><strong>Si el service todavía no se hizo</strong> pero esas horas ya lo consumieron → tocá <em>"Service hecho"</em> en el componente y elegí <em>"Ajustar al estado real"</em> para fijar el remanente real (ej. 38 h).</li>
                <li><strong>Si son horas viejas cuyos services ya están al día</strong> → no hace falta tocar nada.</li>
              </ul>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                {Object.values(postImportNotices).map((n) => (
                  <span key={n.aircraftId} style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    padding: '4px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700,
                    background: darkMode ? 'rgba(245,158,11,0.18)' : 'rgba(254,243,199,0.9)',
                    color: '#92400e',
                    border: '1px solid rgba(245,158,11,0.35)',
                  }}>
                    {n.label} · {n.count} {n.count === 1 ? 'vuelo' : 'vuelos'}
                  </span>
                ))}
              </div>
            </div>
            <button
              onClick={() => setPostImportNotices({})}
              title="Cerrar aviso"
              style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: sub, padding: 4, flexShrink: 0 }}>
              <X style={{ width: 16, height: 16 }} />
            </button>
          </div>
        </div>
      )}

      {/* ── Banner: borrador detectado ────────────────────────────── */}
      {stage === 'idle' && draftAvailable && !editMode && (
        <div style={{ ...card, marginBottom: 16, borderColor: 'rgba(124,58,237,0.4)', background: darkMode ? 'rgba(124,58,237,0.08)' : 'rgba(237,233,254,0.6)' }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ width: 40, height: 40, borderRadius: 12, background: 'rgba(124,58,237,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <History style={{ width: 18, height: 18, color: '#7c3aed' }} />
            </div>
            <div style={{ flex: '1 1 240px', minWidth: 0 }}>
              <p style={{ margin: 0, fontSize: 13, fontWeight: 800 }}>Tenés un borrador con {draftAvailable.planillas.length} {draftAvailable.planillas.length === 1 ? 'planilla pendiente' : 'planillas pendientes'}</p>
              <p style={{ margin: '2px 0 0', fontSize: 12, color: sub }}>
                Se guardó el {new Date(draftAvailable.savedAt).toLocaleString('es-AR')}. Podés continuar donde lo dejaste o empezar de cero.
              </p>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={handleDiscardDraft}
                style={{ padding: '8px 14px', borderRadius: 10, border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0', background: 'transparent', color: sub, cursor: 'pointer', fontWeight: 700, fontSize: 12, fontFamily: 'inherit' }}>
                Descartar
              </button>
              <button onClick={handleContinueDraft}
                style={{ padding: '8px 14px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg,#7c3aed,#6d28d9)', color: 'white', cursor: 'pointer', fontWeight: 700, fontSize: 12, fontFamily: 'inherit' }}>
                Continuar borrador
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Etapa: subir/escanear (idle) ─────────────────────────── */}
      {stage === 'idle' && !editMode && (
        <div style={{ ...card }}>
          {/* Cámara: capture=environment abre la cámara directo en el celular.
              Sin multiple (la cámara saca de a una; se acumulan repitiendo).
              En desktop, capture es ignorado → cae al selector normal. */}
          <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" onChange={handleFilesSelected} style={{ display: 'none' }} />
          {/* Galería: multi-selección de fotos existentes. Sin capture. */}
          <input ref={fileInputRef} type="file" accept="image/*" multiple onChange={handleFilesSelected} style={{ display: 'none' }} />
          {/* Excel/CSV: parseo síncrono en cliente. Single-select. */}
          <input ref={spreadsheetInputRef} type="file" accept=".xlsx,.xls,.csv,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel" onChange={handleSpreadsheetSelected} style={{ display: 'none' }} />
          {pendingFiles.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '32px 16px' }}>
              <Upload style={{ width: 44, height: 44, color: sub, margin: '0 auto 12px' }} />
              <p style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>Subí planillas para importar</p>
              <p style={{ margin: '4px 0 18px', fontSize: 12, color: sub }}>
                Fotos: hasta {MAX_PLANILLAS} por lote (cada foto = una planilla). Excel/CSV: hasta {MAX_PLANILLAS_SPREADSHEET} planillas por lote (el archivo se auto-agrupa por aeronave y mes).
              </p>
              <div style={{ display: 'inline-flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center' }}>
                <button onClick={handlePickCamera}
                  style={{ padding: '12px 24px', borderRadius: 14, border: 'none', cursor: 'pointer', background: 'linear-gradient(135deg,#7c3aed,#6d28d9)', color: 'white', fontWeight: 700, fontSize: 14, fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  <Camera style={{ width: 16, height: 16 }} /> Tomar foto
                </button>
                <button onClick={handlePickFile}
                  style={{ padding: '12px 24px', borderRadius: 14, border: darkMode ? '1px solid rgba(255,255,255,0.15)' : '1px solid #e2e8f0', cursor: 'pointer', background: 'transparent', color: darkMode ? 'white' : '#0f172a', fontWeight: 700, fontSize: 14, fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  <Images style={{ width: 16, height: 16 }} /> Elegir de galería
                </button>
                <button onClick={handlePickSpreadsheet} disabled={parsingSpreadsheet}
                  style={{ padding: '12px 24px', borderRadius: 14, border: darkMode ? '1px solid rgba(255,255,255,0.15)' : '1px solid #e2e8f0', cursor: parsingSpreadsheet ? 'wait' : 'pointer', background: 'transparent', color: darkMode ? 'white' : '#0f172a', fontWeight: 700, fontSize: 14, fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 8, opacity: parsingSpreadsheet ? 0.6 : 1 }}>
                  {parsingSpreadsheet
                    ? <Loader style={{ width: 16, height: 16, animation: 'spin 1s linear infinite' }} />
                    : <FileSpreadsheet style={{ width: 16, height: 16 }} />}
                  Subir Excel/CSV
                </button>
              </div>
              <div style={{ marginTop: 14 }}>
                <button onClick={handleDownloadTemplate}
                  style={{ background: 'transparent', border: 'none', color: '#7c3aed', cursor: 'pointer', fontWeight: 700, fontSize: 12, fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 4, textDecoration: 'underline' }}>
                  <Download style={{ width: 12, height: 12 }} /> Descargar plantilla vacía (CSV)
                </button>
              </div>
            </div>
          ) : (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, gap: 8, flexWrap: 'wrap' }}>
                <p style={{ margin: 0, fontSize: 13, fontWeight: 800 }}>
                  {pendingFiles.length} {pendingFiles.length === 1 ? 'foto lista' : 'fotos listas'} para escanear
                </p>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button onClick={handlePickCamera} disabled={pendingFiles.length >= MAX_PLANILLAS}
                    title="Tomar foto con la cámara"
                    style={{ padding: '8px 14px', borderRadius: 10, border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0', background: 'transparent', color: sub, cursor: pendingFiles.length >= MAX_PLANILLAS ? 'not-allowed' : 'pointer', fontWeight: 700, fontSize: 12, fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 6, opacity: pendingFiles.length >= MAX_PLANILLAS ? 0.5 : 1 }}>
                    <Camera style={{ width: 14, height: 14 }} /> Tomar foto
                  </button>
                  <button onClick={handlePickFile} disabled={pendingFiles.length >= MAX_PLANILLAS}
                    title="Elegir fotos de la galería"
                    style={{ padding: '8px 14px', borderRadius: 10, border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0', background: 'transparent', color: sub, cursor: pendingFiles.length >= MAX_PLANILLAS ? 'not-allowed' : 'pointer', fontWeight: 700, fontSize: 12, fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 6, opacity: pendingFiles.length >= MAX_PLANILLAS ? 0.5 : 1 }}>
                    <Images style={{ width: 14, height: 14 }} /> Galería
                  </button>
                  <button onClick={handleStartBatchScan}
                    style={{ padding: '8px 14px', borderRadius: 10, border: 'none', cursor: 'pointer', background: 'linear-gradient(135deg,#16a34a,#15803d)', color: 'white', fontWeight: 700, fontSize: 12, fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <ScanLine style={{ width: 14, height: 14 }} /> Escanear {pendingFiles.length === 1 ? 'planilla' : 'todas'}
                  </button>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(140px,1fr))', gap: 10 }}>
                {pendingFiles.map((pf, i) => (
                  <div key={pf.id}
                    style={{ position: 'relative', borderRadius: 12, overflow: 'hidden', border: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e2e8f0', background: darkMode ? 'rgba(0,0,0,0.2)' : '#f8fafc', aspectRatio: '4/3' }}>
                    {pf.thumbnail ? (
                      <img src={pf.thumbnail} alt={`planilla ${i + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                    ) : (
                      <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: sub }}>{pf.file?.name || 'foto'}</div>
                    )}
                    <button onClick={() => removePendingFile(pf.id)} title="Quitar"
                      style={{ position: 'absolute', top: 4, right: 4, background: 'rgba(0,0,0,0.6)', border: 'none', borderRadius: '50%', width: 22, height: 22, cursor: 'pointer', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <X style={{ width: 12, height: 12 }} />
                    </button>
                    <div style={{ position: 'absolute', bottom: 4, left: 4, background: 'rgba(0,0,0,0.55)', color: 'white', fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 6 }}>
                      #{i + 1}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Etapa: scanning ─────────────────────────────────────── */}
      {stage === 'scanning' && (
        <div style={{ ...card }}>
          <div style={{ textAlign: 'center', marginBottom: 20 }}>
            <Loader style={{ width: 36, height: 36, color: '#7c3aed', animation: 'spin 1s linear infinite', margin: '0 auto 12px' }} />
            <p style={{ margin: 0, fontSize: 14, fontWeight: 700 }}>
              Transcribiendo con Claude Vision — {scanProgress.done} de {scanProgress.total}
            </p>
            <p style={{ margin: '4px 0 0', fontSize: 12, color: sub }}>Hasta 3 planillas en paralelo. Cada una tarda 15-40 segundos.</p>
          </div>
          <div style={{ height: 6, borderRadius: 3, background: darkMode ? 'rgba(255,255,255,0.08)' : '#e2e8f0', overflow: 'hidden', marginBottom: 16 }}>
            <div style={{
              height: '100%',
              width: `${scanProgress.total ? (scanProgress.done / scanProgress.total) * 100 : 0}%`,
              background: 'linear-gradient(90deg,#7c3aed,#a855f7)',
              transition: 'width 300ms ease',
            }} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(220px,1fr))', gap: 10 }}>
            {planillas.map((p, i) => {
              const b = statusBadge(p);
              return (
                <div key={p.id} style={{ display: 'flex', gap: 10, padding: 10, borderRadius: 10, border: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e2e8f0', background: darkMode ? 'rgba(0,0,0,0.15)' : '#f8fafc' }}>
                  {p.thumbnail ? (
                    <img src={p.thumbnail} alt="" style={{ width: 44, height: 44, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }} />
                  ) : (
                    <div style={{ width: 44, height: 44, borderRadius: 8, background: darkMode ? 'rgba(255,255,255,0.05)' : '#e2e8f0', flexShrink: 0 }} />
                  )}
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <p style={{ margin: 0, fontSize: 12, fontWeight: 700 }}>Planilla {i + 1}</p>
                    <span style={chip(b.bg, b.color)}>{b.icon} {b.label}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Etapa: review (multi o edit) ──────────────────────── */}
      {stage === 'review' && selected && (
        <div style={{ display: showSidebar && isDesktopReview ? 'grid' : 'block', gridTemplateColumns: showSidebar && isDesktopReview ? '280px 1fr' : undefined, gap: 16 }}>
          {/* SIDEBAR — multi */}
          {showSidebar && (
            <div style={{ ...card, padding: 12, alignSelf: 'flex-start' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                <p style={{ margin: 0, fontSize: 12, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', color: sub }}>
                  Planillas ({planillas.length})
                </p>
                <div style={{ display: 'flex', gap: 4 }}>
                  <button onClick={handlePickCamera}
                    title={planillas.length + pendingFiles.length >= MAX_PLANILLAS ? 'Límite de fotos alcanzado' : 'Tomar foto'}
                    disabled={planillas.length + pendingFiles.length >= MAX_PLANILLAS}
                    style={{ background: 'transparent', border: 'none', cursor: planillas.length + pendingFiles.length >= MAX_PLANILLAS ? 'not-allowed' : 'pointer', color: sub, padding: 4, opacity: planillas.length + pendingFiles.length >= MAX_PLANILLAS ? 0.4 : 1 }}>
                    <Camera style={{ width: 14, height: 14 }} />
                  </button>
                  <button onClick={handlePickFile}
                    title={planillas.length + pendingFiles.length >= MAX_PLANILLAS ? 'Límite de fotos alcanzado' : 'Elegir de galería'}
                    disabled={planillas.length + pendingFiles.length >= MAX_PLANILLAS}
                    style={{ background: 'transparent', border: 'none', cursor: planillas.length + pendingFiles.length >= MAX_PLANILLAS ? 'not-allowed' : 'pointer', color: sub, padding: 4, opacity: planillas.length + pendingFiles.length >= MAX_PLANILLAS ? 0.4 : 1 }}>
                    <Images style={{ width: 14, height: 14 }} />
                  </button>
                  <button onClick={handlePickSpreadsheet}
                    title={planillas.length >= MAX_PLANILLAS_SPREADSHEET ? 'Límite de planillas del lote alcanzado' : 'Subir Excel/CSV'}
                    disabled={planillas.length >= MAX_PLANILLAS_SPREADSHEET || parsingSpreadsheet}
                    style={{ background: 'transparent', border: 'none', cursor: (planillas.length >= MAX_PLANILLAS_SPREADSHEET || parsingSpreadsheet) ? 'not-allowed' : 'pointer', color: sub, padding: 4, opacity: (planillas.length >= MAX_PLANILLAS_SPREADSHEET || parsingSpreadsheet) ? 0.4 : 1 }}>
                    {parsingSpreadsheet
                      ? <Loader style={{ width: 14, height: 14, animation: 'spin 1s linear infinite' }} />
                      : <FileSpreadsheet style={{ width: 14, height: 14 }} />}
                  </button>
                </div>
              </div>
              <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" onChange={handleFilesSelected} style={{ display: 'none' }} />
              <input ref={fileInputRef} type="file" accept="image/*" multiple onChange={handleFilesSelected} style={{ display: 'none' }} />
              <input ref={spreadsheetInputRef} type="file" accept=".xlsx,.xls,.csv,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel" onChange={handleSpreadsheetSelected} style={{ display: 'none' }} />
              <div style={{ display: 'flex', flexDirection: isDesktopReview ? 'column' : 'row', gap: 8, overflowX: isDesktopReview ? 'visible' : 'auto', paddingBottom: isDesktopReview ? 0 : 4 }}>
                {orderedIndices.map(idx => {
                  const p = planillas[idx];
                  const b = statusBadge(p);
                  const active = idx === selectedIdx;
                  const lowCount = p.rows.reduce((n, r) => n + (r._low && Object.values(r._low).some(Boolean) ? 1 : 0), 0);
                  return (
                    <button key={p.id} onClick={() => setSelectedIdx(idx)}
                      style={{
                        display: 'flex', alignItems: 'flex-start', gap: 8,
                        padding: 10, borderRadius: 10,
                        border: active
                          ? '1.5px solid #7c3aed'
                          : (darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e2e8f0'),
                        background: active
                          ? (darkMode ? 'rgba(124,58,237,0.12)' : 'rgba(237,233,254,0.5)')
                          : 'transparent',
                        cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit',
                        minWidth: isDesktopReview ? 0 : 220, flexShrink: 0, color: text,
                      }}>
                      {p.thumbnail ? (
                        <img src={p.thumbnail} alt="" style={{ width: 38, height: 38, borderRadius: 6, objectFit: 'cover', flexShrink: 0 }} />
                      ) : p.source === 'spreadsheet' ? (
                        <div title="Origen: Excel/CSV" style={{ width: 38, height: 38, borderRadius: 6, background: darkMode ? 'rgba(22,163,74,0.15)' : 'rgba(220,252,231,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <FileSpreadsheet style={{ width: 18, height: 18, color: '#16a34a' }} />
                        </div>
                      ) : (
                        <div style={{ width: 38, height: 38, borderRadius: 6, background: darkMode ? 'rgba(255,255,255,0.05)' : '#e2e8f0', flexShrink: 0 }} />
                      )}
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <p style={{ margin: 0, fontSize: 11, fontWeight: 800, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          Planilla {idx + 1}
                        </p>
                        <p style={{ margin: '2px 0 4px', fontSize: 10, color: sub, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {p.aircraftId ? aircraftLabel(p.aircraftId) : (p.registrationText || '—')} · {p.month ? `${monthLabel(p.month)} ${p.year || ''}` : '—'}
                        </p>
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                          <span style={chip(b.bg, b.color)}>{b.icon} {b.label}</span>
                          {p.status !== 'confirmed' && (
                            <span style={chip(darkMode ? 'rgba(255,255,255,0.06)' : '#f1f5f9', sub)}>
                              {p.rows.length} filas{lowCount ? ` · ${lowCount} dudosas` : ''}
                            </span>
                          )}
                          {p.status !== 'confirmed' && hasAnyDuplicate(idx) && (
                            <span style={chip('rgba(245,158,11,0.15)', '#d97706')}
                              title="Ya existe una importación con la misma aeronave y período (o hay otra planilla del lote con la misma clave). Vas a poder decidir al confirmar.">
                              <AlertTriangle style={{ width: 10, height: 10 }} /> Posible duplicado
                            </span>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* ZONA PRINCIPAL: header + tabla + acciones per-planilla */}
          <div style={{ minWidth: 0 }}>
            <div style={{ ...card, marginBottom: 16 }}>
              <p style={{ margin: '0 0 12px', fontSize: 13, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', color: sub }}>
                {editMode ? 'Encabezado del lote' : `Planilla ${selectedIdx + 1} de ${planillas.length} — Encabezado detectado`}
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: 12 }}>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: sub }}>Aeronave</label>
                  <select value={selected.aircraftId} onChange={e => updateSelected({ aircraftId: e.target.value })}
                    disabled={editMode || selected.status === 'confirmed'}
                    style={{ ...inp(false), padding: '8px 10px', fontSize: 13, opacity: (editMode || selected.status === 'confirmed') ? 0.6 : 1, cursor: (editMode || selected.status === 'confirmed') ? 'not-allowed' : 'pointer' }}>
                    <option value="">— elegí aeronave —</option>
                    {aircraftList.map(a => (
                      <option key={a.id} value={a.id}>{a.registration ? `${a.registration} — ` : ''}{a.manufacturer} {a.model_name}</option>
                    ))}
                  </select>
                  {editMode ? (
                    <p style={{ margin: '4px 0 0', fontSize: 11, color: sub }}>
                      La aeronave del lote no se puede cambiar al editar.
                    </p>
                  ) : selected.registrationText && (
                    <p style={{ margin: '4px 0 0', fontSize: 11, color: sub }}>
                      Vision detectó: <strong>{selected.registrationText}</strong>
                    </p>
                  )}
                </div>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: sub }}>Año</label>
                  <input value={selected.year} onChange={e => updateSelected({ year: e.target.value.replace(/[^\d]/g, '').slice(0, 4) })}
                    disabled={selected.status === 'confirmed'}
                    style={{ ...inp(false), padding: '8px 10px', fontSize: 13, opacity: selected.status === 'confirmed' ? 0.6 : 1 }} placeholder="2025" />
                </div>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: sub }}>Mes</label>
                  <select value={selected.month} onChange={e => updateSelected({ month: e.target.value })}
                    disabled={selected.status === 'confirmed'}
                    style={{ ...inp(false), padding: '8px 10px', fontSize: 13, opacity: selected.status === 'confirmed' ? 0.6 : 1 }}>
                    <option value="">—</option>
                    {['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'].map((m, i) => (
                      <option key={i} value={i + 1}>{i + 1} — {m}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: sub }}>Nota del lote (opcional)</label>
                  <input value={selected.note} onChange={e => updateSelected({ note: e.target.value })}
                    disabled={selected.status === 'confirmed'}
                    style={{ ...inp(false), padding: '8px 10px', fontSize: 13, opacity: selected.status === 'confirmed' ? 0.6 : 1 }} placeholder="ej. planilla Agosto 2025" />
                </div>
              </div>
              {/* Si la planilla falló el scan mostramos aviso + acciones específicas */}
              {selected.status === 'failed' && (
                <div style={{ marginTop: 12, padding: 12, borderRadius: 10, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)' }}>
                  <p style={{ margin: '0 0 8px', fontSize: 12, fontWeight: 700, color: '#dc2626' }}>
                    No se pudo procesar la foto: {selected.error || 'error desconocido'}
                  </p>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={() => handleRetryScan(selectedIdx)} disabled={!selected.file}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 8, border: '1px solid rgba(124,58,237,0.4)', background: 'transparent', color: '#7c3aed', cursor: selected.file ? 'pointer' : 'not-allowed', fontWeight: 700, fontSize: 12, fontFamily: 'inherit', opacity: selected.file ? 1 : 0.5 }}>
                      <RefreshCw style={{ width: 12, height: 12 }} /> Reintentar
                    </button>
                    <button onClick={() => handleDiscardPlanilla(selectedIdx)}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 8, border: '1px solid rgba(239,68,68,0.4)', background: 'transparent', color: '#ef4444', cursor: 'pointer', fontWeight: 700, fontSize: 12, fontFamily: 'inherit' }}>
                      <Trash2 style={{ width: 12, height: 12 }} /> Descartar
                    </button>
                  </div>
                </div>
              )}
              {selected.status === 'confirmed' && (
                <div style={{ marginTop: 12, padding: 10, borderRadius: 10, background: 'rgba(22,163,74,0.1)', border: '1px solid rgba(22,163,74,0.3)', display: 'flex', gap: 8, alignItems: 'center' }}>
                  <Check style={{ width: 14, height: 14, color: '#16a34a' }} />
                  <span style={{ fontSize: 12, fontWeight: 700, color: '#16a34a' }}>
                    Planilla ya confirmada e importada. Podés revertirla desde "Lotes importados".
                  </span>
                </div>
              )}
            </div>

            {/* Tabla de revisión — se muestra siempre que haya filas o el scan haya funcionado */}
            {selected.status !== 'failed' && (
              <div style={{ ...card, marginBottom: 16, padding: 0, overflow: 'hidden' }}>
                <div style={{ padding: '14px 20px', borderBottom: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  <div style={{ minWidth: 0, flex: '1 1 220px' }}>
                    <p style={{ margin: 0, fontSize: 13, fontWeight: 800 }}>
                      {editMode ? `${selected.rows.length} vuelos en el lote` : `${selected.rows.length} vuelos detectados`}
                    </p>
                    <p style={{ margin: '2px 0 0', fontSize: 11, color: sub }}>
                      {editMode
                        ? `Corregí lo que esté mal, agregá o quitá filas. Aprox. ${selectedTotalHours.toFixed(1)} hs en total.`
                        : `Las celdas resaltadas en ámbar son de baja confianza — revisalas antes de confirmar. Aprox. ${selectedTotalHours.toFixed(1)} hs en total.`}
                    </p>
                  </div>
                  <button onClick={addEmptyRowSelected} disabled={selected.status === 'confirmed'}
                    style={{ padding: '6px 12px', borderRadius: 10, border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0', background: 'transparent', color: sub, cursor: selected.status === 'confirmed' ? 'not-allowed' : 'pointer', fontWeight: 700, fontSize: 12, fontFamily: 'inherit', whiteSpace: 'nowrap', flexShrink: 0, opacity: selected.status === 'confirmed' ? 0.5 : 1 }}>
                    + Fila vacía
                  </button>
                </div>

                <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                  <table style={{
                    width: '100%',
                    minWidth: isDesktopReview ? 2200 : 1500,
                    borderCollapse: 'collapse',
                    fontSize: isDesktopReview ? 13 : 11,
                  }}>
                    <thead>
                      <tr style={{ background: darkMode ? 'rgba(255,255,255,0.04)' : '#f8fafc' }}>
                        {['Día','Piloto','Vincular','Finalidad','Sale','Desde','Hasta','Llega','T. Vuelo','Atrz.','Aceite','Instructor','Vinc.','Hobbs ini','Hobbs fin','Pago',''].map((h,i) => (
                          <th key={i} style={{ padding: isDesktopReview ? '10px 10px' : '8px 6px', textAlign: 'left', fontSize: isDesktopReview ? 11 : 10, fontWeight: 800, textTransform: 'uppercase', color: sub, whiteSpace: 'nowrap', borderBottom: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e2e8f0' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {selected.rows.map((r, i) => (
                        <tr key={i} style={{ borderBottom: darkMode ? '1px solid rgba(255,255,255,0.04)' : '1px solid #f1f5f9' }}>
                          <td style={tdStyle(64, 70)}>
                            <input value={r.day} onChange={e => updateSelectedRow(i, { day: e.target.value.replace(/[^\d]/g, '').slice(0,2) })} disabled={selected.status === 'confirmed'} style={{ ...inp(r._low?.day), textAlign: 'center' }} />
                          </td>
                          <td style={tdStyle(130, 180)}>
                            <input value={r.pilot} onChange={e => updateSelectedRow(i, { pilot: e.target.value })} disabled={selected.status === 'confirmed'} style={inp(r._low?.pilot)} />
                          </td>
                          <td style={tdStyle(140, 200)}>
                            <select value={r.recorded_by} onChange={e => updateSelectedRow(i, { recorded_by: e.target.value })} disabled={selected.status === 'confirmed'} style={inp(false)}>
                              <option value="">— no vincular —</option>
                              {usersList.map(u => (
                                <option key={u.id} value={u.id}>{u.name}</option>
                              ))}
                            </select>
                          </td>
                          <td style={tdStyle(150, 200)}>
                            <select value={r.purpose} onChange={e => updateSelectedRow(i, { purpose: e.target.value })} disabled={selected.status === 'confirmed'} style={inp(r._low?.purpose)}>
                              {PURPOSE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                            {r.purpose === 'Otros' && (
                              <input value={r.purpose_other} onChange={e => updateSelectedRow(i, { purpose_other: e.target.value })}
                                placeholder="Detalle" disabled={selected.status === 'confirmed'}
                                style={{ ...inp(false), marginTop: 4 }} />
                            )}
                          </td>
                          <td style={tdStyle(78, 90)}>
                            <input value={r.dep_time} onChange={e => updateSelectedRow(i, { dep_time: e.target.value })} disabled={selected.status === 'confirmed'} style={inp(r._low?.dep_time)} />
                          </td>
                          <td style={tdStyle(90, 100)}>
                            <input value={r.from} onChange={e => updateSelectedRow(i, { from: e.target.value })} disabled={selected.status === 'confirmed'} style={inp(r._low?.from)} />
                          </td>
                          <td style={tdStyle(90, 100)}>
                            <input value={r.to} onChange={e => updateSelectedRow(i, { to: e.target.value })} disabled={selected.status === 'confirmed'} style={inp(r._low?.to)} />
                          </td>
                          <td style={tdStyle(78, 90)}>
                            <input value={r.arr_time} onChange={e => updateSelectedRow(i, { arr_time: e.target.value })} disabled={selected.status === 'confirmed'} style={inp(r._low?.arr_time)} />
                          </td>
                          <td style={tdStyle(82, 100)}>
                            <input value={r.flight_time} onChange={e => updateSelectedRow(i, { flight_time: e.target.value })} disabled={selected.status === 'confirmed'} style={inp(r._low?.flight_time)} />
                          </td>
                          <td style={tdStyle(60, 70)}>
                            <input value={r.landings} onChange={e => updateSelectedRow(i, { landings: e.target.value.replace(/[^\d]/g, '') })} disabled={selected.status === 'confirmed'} style={{ ...inp(r._low?.landings), textAlign: 'center' }} />
                          </td>
                          <td style={tdStyle(70, 90)}>
                            <input value={r.oil} onChange={e => updateSelectedRow(i, { oil: e.target.value })} disabled={selected.status === 'confirmed'} style={inp(r._low?.oil)} />
                          </td>
                          <td style={tdStyle(120, 180)}>
                            <input value={r.instructor} onChange={e => updateSelectedRow(i, { instructor: e.target.value })} disabled={selected.status === 'confirmed'} style={inp(r._low?.instructor)} />
                          </td>
                          <td style={tdStyle(140, 200)}>
                            <select value={r.instructor_user_id} onChange={e => updateSelectedRow(i, { instructor_user_id: e.target.value })} disabled={selected.status === 'confirmed'} style={inp(false)}>
                              <option value="">— no vincular —</option>
                              {usersList.map(u => (
                                <option key={u.id} value={u.id}>{u.name}</option>
                              ))}
                            </select>
                          </td>
                          <td style={tdStyle(80, 110)}>
                            <input value={r.hobbs_start} onChange={e => updateSelectedRow(i, { hobbs_start: e.target.value.replace(/[^\d.,]/g, '').replace(',', '.') })} disabled={selected.status === 'confirmed'} style={inp(r._low?.hobbs_start)} />
                          </td>
                          <td style={tdStyle(80, 110)}>
                            <input value={r.hobbs_end} onChange={e => updateSelectedRow(i, { hobbs_end: e.target.value.replace(/[^\d.,]/g, '').replace(',', '.') })} disabled={selected.status === 'confirmed'} style={inp(r._low?.hobbs_end)} />
                          </td>
                          <td style={tdStyle(130, 180)}>
                            <select value={r.payment_method} onChange={e => updateSelectedRow(i, { payment_method: e.target.value })} disabled={selected.status === 'confirmed'} style={inp(r._low?.payment_method)}>
                              {PAYMENT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                          </td>
                          <td style={{ ...tdStyle(44, 50), textAlign: 'center' }}>
                            <button onClick={() => removeSelectedRow(i)} title="Descartar fila" disabled={selected.status === 'confirmed'}
                              style={{ background: 'transparent', border: 'none', cursor: selected.status === 'confirmed' ? 'not-allowed' : 'pointer', color: '#ef4444', padding: 4, opacity: selected.status === 'confirmed' ? 0.4 : 1 }}>
                              <Trash2 style={{ width: 14, height: 14 }} />
                            </button>
                          </td>
                        </tr>
                      ))}
                      {!selected.rows.length && (
                        <tr>
                          <td colSpan={17} style={{ padding: 28, textAlign: 'center', color: sub, fontSize: 12 }}>
                            Sin filas. Agregá una fila vacía o descartá esta planilla.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Acciones per-planilla / edit */}
            {editMode ? (
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginBottom: 24 }}>
                {/* En edit mode, "Cancelar" descarta los cambios locales y
                    vuelve a idle. NO va por handleStartOver (que abriría el
                    modal "Empezar de nuevo" porque la planilla cargada tiene
                    status='scanned' — trip del "somethingToLose"). El lote
                    real en la DB no se toca al cancelar una edición. */}
                <button onClick={performStartOver}
                  style={{ padding: '10px 18px', borderRadius: 12, border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0', background: 'transparent', color: sub, cursor: 'pointer', fontWeight: 700, fontSize: 13, fontFamily: 'inherit' }}>
                  Cancelar
                </button>
                <button onClick={handleSaveEdit} disabled={!selected.rows.length}
                  style={{ padding: '10px 20px', borderRadius: 12, border: 'none', cursor: selected.rows.length ? 'pointer' : 'not-allowed', background: 'linear-gradient(135deg,#7c3aed,#6d28d9)', color: 'white', fontWeight: 700, fontSize: 13, fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 8, opacity: selected.rows.length ? 1 : 0.5 }}>
                  <Check style={{ width: 14, height: 14 }} /> Guardar cambios
                </button>
              </div>
            ) : selected.status !== 'confirmed' && selected.status !== 'failed' && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginBottom: 24, flexWrap: 'wrap' }}>
                <button onClick={() => handleDiscardPlanilla(selectedIdx)}
                  style={{ padding: '10px 18px', borderRadius: 12, border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0', background: 'transparent', color: '#ef4444', cursor: 'pointer', fontWeight: 700, fontSize: 13, fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <Trash2 style={{ width: 14, height: 14 }} /> Descartar planilla
                </button>
                <button onClick={() => handleConfirmPlanilla(selectedIdx)}
                  disabled={selected.status === 'confirming' || !selected.rows.length}
                  style={{ padding: '10px 20px', borderRadius: 12, border: 'none', cursor: (selected.status === 'confirming' || !selected.rows.length) ? 'wait' : 'pointer', background: 'linear-gradient(135deg,#16a34a,#15803d)', color: 'white', fontWeight: 700, fontSize: 13, fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 8, opacity: (selected.status === 'confirming' || !selected.rows.length) ? 0.6 : 1 }}>
                  {selected.status === 'confirming'
                    ? <Loader style={{ width: 14, height: 14, animation: 'spin 1s linear infinite' }} />
                    : <Check style={{ width: 14, height: 14 }} />}
                  Confirmar planilla ({selected.rows.length} vuelos)
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Etapa: importing (solo edit mode) ───────────────────── */}
      {stage === 'importing' && (
        <div style={{ ...card, textAlign: 'center', padding: 48 }}>
          <Loader style={{ width: 36, height: 36, color: '#7c3aed', animation: 'spin 1s linear infinite', margin: '0 auto 12px' }} />
          <p style={{ margin: 0, fontSize: 14, fontWeight: 700 }}>
            {editMode ? 'Guardando cambios...' : 'Importando vuelos...'}
          </p>
        </div>
      )}

      {/* ── Etapa: done (solo edit mode) ────────────────────────── */}
      {stage === 'done' && (
        <div style={{ ...card, textAlign: 'center', padding: 48, marginBottom: 24, borderColor: 'rgba(22,163,74,0.4)' }}>
          <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'rgba(22,163,74,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
            <Check style={{ width: 28, height: 28, color: '#16a34a' }} />
          </div>
          <p style={{ margin: 0, fontSize: 15, fontWeight: 800 }}>
            {editMode ? 'Cambios guardados' : 'Importación completa'}
          </p>
          <p style={{ margin: '4px 0 18px', fontSize: 12, color: sub }}>
            {editMode
              ? 'El lote quedó actualizado. Las horas de los pilotos vinculados ya reflejan los cambios.'
              : 'Los vuelos quedan en el libro con badge IMPORTADO. Podés revertir el lote desde "Lotes importados".'}
          </p>
          <button onClick={performStartOver}
            style={{ padding: '10px 18px', borderRadius: 12, border: 'none', background: 'linear-gradient(135deg,#16a34a,#15803d)', color: 'white', cursor: 'pointer', fontWeight: 700, fontSize: 13, fontFamily: 'inherit' }}>
            {editMode ? 'Volver al listado' : 'Importar otra planilla'}
          </button>
        </div>
      )}

      {/* ── Lotes importados ──────────────────────────────────── */}
      <div style={{ ...card }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, gap: 12, flexWrap: 'wrap' }}>
          <p style={{ margin: 0, fontSize: 13, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', color: sub, display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
            <History style={{ width: 14, height: 14, flexShrink: 0 }} /> Lotes importados
          </p>
          <button onClick={loadBatches}
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: sub, fontSize: 12, fontWeight: 700, fontFamily: 'inherit', flexShrink: 0, whiteSpace: 'nowrap' }}>
            Refrescar
          </button>
        </div>
        {loadingBatches ? (
          <div style={{ textAlign: 'center', padding: 24 }}>
            <Loader style={{ width: 22, height: 22, color: sub, animation: 'spin 1s linear infinite' }} />
          </div>
        ) : !batches.length ? (
          <p style={{ margin: 0, fontSize: 12, color: sub }}>Todavía no se importó ninguna planilla.</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr>
                  {['Fecha import','Aeronave','Período','Vuelos','Importó','Nota',''].map((h,i) => (
                    <th key={i} style={{ padding: '8px 10px', textAlign: 'left', fontSize: 10, fontWeight: 800, textTransform: 'uppercase', color: sub, borderBottom: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e2e8f0' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {batches.map(b => (
                  <tr key={b.id} style={{ borderBottom: darkMode ? '1px solid rgba(255,255,255,0.04)' : '1px solid #f1f5f9' }}>
                    <td style={{ padding: '8px 10px' }}>{new Date(b.imported_at).toLocaleString('es-AR')}</td>
                    <td style={{ padding: '8px 10px' }}>{b.registration || b.aircraft || '—'}</td>
                    <td style={{ padding: '8px 10px', fontFamily: 'monospace' }}>{b.period_label || '—'}</td>
                    <td style={{ padding: '8px 10px', fontWeight: 700, color: '#7c3aed' }}>{b.rows_count}</td>
                    <td style={{ padding: '8px 10px' }}>{b.imported_by_name || '—'}</td>
                    <td style={{ padding: '8px 10px', color: sub, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.note || '—'}</td>
                    <td style={{ padding: '8px 10px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button onClick={() => handleEditBatch(b)} disabled={loadingEdit}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 10px', borderRadius: 8, border: '1px solid rgba(124,58,237,0.4)', background: 'transparent', color: '#7c3aed', cursor: loadingEdit ? 'wait' : 'pointer', fontWeight: 700, fontSize: 11, fontFamily: 'inherit', marginRight: 6 }}>
                        {loadingEdit ? <Loader style={{ width: 12, height: 12, animation: 'spin 1s linear infinite' }} /> : <Pencil style={{ width: 12, height: 12 }} />} Editar
                      </button>
                      <button onClick={() => setConfirmDelete(b)}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 10px', borderRadius: 8, border: '1px solid rgba(239,68,68,0.4)', background: 'transparent', color: '#ef4444', cursor: 'pointer', fontWeight: 700, fontSize: 11, fontFamily: 'inherit' }}>
                        <Trash2 style={{ width: 12, height: 12 }} /> Revertir
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Barra sticky "Estado del lote" (modo multi) ───────── */}
      {bulkFooterVisible && (
        <div style={{
          position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 40,
          background: darkMode ? 'rgba(17,24,39,0.95)' : 'rgba(255,255,255,0.98)',
          borderTop: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e2e8f0',
          backdropFilter: 'blur(6px)',
          padding: '10px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 12, flexWrap: 'wrap',
        }}>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', fontSize: 12, fontWeight: 700 }}>
            <span style={chip('rgba(22,163,74,0.15)', '#16a34a')}>✓ {counts.confirmed} confirmadas</span>
            <span style={chip('rgba(6,182,212,0.15)', '#0891b2')}>● {counts.ready} listas</span>
            <span style={chip('rgba(245,158,11,0.15)', '#d97706')}>⚠ {counts.pendingReview} pendientes</span>
            {counts.scanning > 0 && <span style={chip('rgba(124,58,237,0.15)', '#7c3aed')}>… {counts.scanning} procesando</span>}
            {counts.failed > 0 && <span style={chip('rgba(239,68,68,0.15)', '#dc2626')}>! {counts.failed} falla</span>}
          </div>
        </div>
      )}

      {/* ── Modal de confirmación de reversión de lote ────────── */}
      <AnimatePresence>
        {confirmDelete && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setConfirmDelete(null)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
            <motion.div
              initial={{ scale: 0.95, y: 16 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 16 }}
              onClick={e => e.stopPropagation()}
              style={{ background: darkMode ? '#111827' : 'white', borderRadius: 20, padding: 24, maxWidth: 440, width: '100%', boxShadow: '0 24px 60px rgba(0,0,0,0.3)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                <div style={{ width: 40, height: 40, borderRadius: 12, background: 'rgba(239,68,68,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <AlertTriangle style={{ width: 18, height: 18, color: '#ef4444' }} />
                </div>
                <p style={{ margin: 0, fontSize: 15, fontWeight: 800, color: text }}>Revertir importación</p>
              </div>
              <p style={{ margin: '0 0 16px', fontSize: 13, color: sub, lineHeight: 1.5 }}>
                Esto va a borrar los <strong style={{ color: text }}>{confirmDelete.rows_count} vuelos</strong> de la planilla {confirmDelete.period_label || ''} ({confirmDelete.registration || confirmDelete.aircraft || ''}). Los vuelos normales del libro no se tocan. Acción irreversible.
              </p>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button onClick={() => setConfirmDelete(null)} disabled={!!deletingId}
                  style={{ padding: '10px 18px', borderRadius: 12, border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0', background: 'transparent', color: sub, cursor: 'pointer', fontWeight: 700, fontSize: 13, fontFamily: 'inherit' }}>
                  Cancelar
                </button>
                <button onClick={() => handleDeleteBatch(confirmDelete)} disabled={!!deletingId}
                  style={{ padding: '10px 18px', borderRadius: 12, border: 'none', background: '#ef4444', color: 'white', cursor: deletingId ? 'wait' : 'pointer', fontWeight: 700, fontSize: 13, fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  {deletingId ? <Loader style={{ width: 14, height: 14, animation: 'spin 1s linear infinite' }} /> : <Trash2 style={{ width: 14, height: 14 }} />}
                  Revertir lote
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Modal: confirmar "Empezar de nuevo" ──────────────── */}
      <AnimatePresence>
        {confirmStartOver && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setConfirmStartOver(false)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
            <motion.div
              initial={{ scale: 0.95, y: 16 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 16 }}
              onClick={e => e.stopPropagation()}
              style={{ background: darkMode ? '#111827' : 'white', borderRadius: 20, padding: 24, maxWidth: 440, width: '100%', boxShadow: '0 24px 60px rgba(0,0,0,0.3)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                <div style={{ width: 40, height: 40, borderRadius: 12, background: 'rgba(245,158,11,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <AlertTriangle style={{ width: 18, height: 18, color: '#d97706' }} />
                </div>
                <p style={{ margin: 0, fontSize: 15, fontWeight: 800, color: text }}>Empezar de nuevo</p>
              </div>
              <p style={{ margin: '0 0 16px', fontSize: 13, color: sub, lineHeight: 1.5 }}>
                Esto va a descartar el lote actual (planillas no confirmadas y el borrador). Las que ya confirmaste quedan intactas en la DB.
              </p>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button onClick={() => setConfirmStartOver(false)}
                  style={{ padding: '10px 18px', borderRadius: 12, border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0', background: 'transparent', color: sub, cursor: 'pointer', fontWeight: 700, fontSize: 13, fontFamily: 'inherit' }}>
                  Cancelar
                </button>
                <button onClick={performStartOver}
                  style={{ padding: '10px 18px', borderRadius: 12, border: 'none', background: '#f59e0b', color: 'white', cursor: 'pointer', fontWeight: 700, fontSize: 13, fontFamily: 'inherit' }}>
                  Sí, empezar de nuevo
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Modal: aviso de posible duplicado ─────────────────── */}
      <AnimatePresence>
        {duplicateWarning && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setDuplicateWarning(null)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
            <motion.div
              initial={{ scale: 0.95, y: 16 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 16 }}
              onClick={e => e.stopPropagation()}
              style={{ background: darkMode ? '#111827' : 'white', borderRadius: 20, padding: 24, maxWidth: 520, width: '100%', boxShadow: '0 24px 60px rgba(0,0,0,0.3)', maxHeight: '85vh', overflowY: 'auto' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                <div style={{ width: 40, height: 40, borderRadius: 12, background: 'rgba(245,158,11,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <AlertTriangle style={{ width: 18, height: 18, color: '#d97706' }} />
                </div>
                <p style={{ margin: 0, fontSize: 15, fontWeight: 800, color: text }}>
                  Posible duplicado
                </p>
              </div>

              {(() => {
                const p = planillas[duplicateWarning.idx];
                const acLabel = p ? aircraftLabel(p.aircraftId) : '—';
                const periodStr = p ? `${monthLabel(p.month)} ${p.year || ''}`.trim() : '';
                return (
                  <>
                    <p style={{ margin: '0 0 10px', fontSize: 13, color: sub, lineHeight: 1.5 }}>
                      La planilla <strong style={{ color: text }}>{acLabel}</strong>
                      {periodStr && <> · <strong style={{ color: text }}>{periodStr}</strong></>} ya aparece en el sistema. Puede ser una recarga legítima (planilla corregida) o un duplicado real.
                    </p>
                    {duplicateWarning.existingBatches.length > 0 && (
                      <div style={{ margin: '10px 0', padding: 12, borderRadius: 10, background: darkMode ? 'rgba(255,255,255,0.04)' : '#f8fafc', border: darkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid #e2e8f0' }}>
                        <p style={{ margin: '0 0 6px', fontSize: 11, fontWeight: 800, textTransform: 'uppercase', color: sub, letterSpacing: '0.06em' }}>
                          Ya importado{duplicateWarning.existingBatches.length > 1 ? 's' : ''}
                        </p>
                        {duplicateWarning.existingBatches.map(b => (
                          <p key={b.id} style={{ margin: '2px 0', fontSize: 12, color: text }}>
                            • {b.registration || b.aircraft || '—'} · {b.period_label || '—'} · {b.rows_count} vuelos · {b.imported_by_name || '—'} · {new Date(b.imported_at).toLocaleDateString('es-AR')}
                          </p>
                        ))}
                      </div>
                    )}
                    {duplicateWarning.intraLotPlanillas.length > 0 && (
                      <div style={{ margin: '10px 0', padding: 12, borderRadius: 10, background: darkMode ? 'rgba(245,158,11,0.06)' : 'rgba(254,243,199,0.5)', border: '1px solid rgba(245,158,11,0.3)' }}>
                        <p style={{ margin: '0 0 6px', fontSize: 11, fontWeight: 800, textTransform: 'uppercase', color: '#d97706', letterSpacing: '0.06em' }}>
                          En este mismo lote
                        </p>
                        {duplicateWarning.intraLotPlanillas.map(({ idx: i }) => (
                          <p key={i} style={{ margin: '2px 0', fontSize: 12, color: text }}>
                            • Planilla {i + 1} tiene la misma aeronave y período
                          </p>
                        ))}
                      </div>
                    )}
                    <p style={{ margin: '10px 0 16px', fontSize: 12, color: sub, lineHeight: 1.5 }}>
                      Podés importarla igual (queda como lote adicional; los vuelos se cargan de nuevo) o descartar esta planilla del lote.
                    </p>
                  </>
                );
              })()}

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
                <button onClick={() => { const i = duplicateWarning.idx; setDuplicateWarning(null); handleDiscardPlanilla(i); }}
                  style={{ padding: '10px 18px', borderRadius: 12, border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0', background: 'transparent', color: '#ef4444', cursor: 'pointer', fontWeight: 700, fontSize: 13, fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <Trash2 style={{ width: 14, height: 14 }} /> Descartar planilla
                </button>
                <button onClick={() => setDuplicateWarning(null)}
                  style={{ padding: '10px 18px', borderRadius: 12, border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0', background: 'transparent', color: sub, cursor: 'pointer', fontWeight: 700, fontSize: 13, fontFamily: 'inherit' }}>
                  Cancelar
                </button>
                <button onClick={() => { const i = duplicateWarning.idx; setDuplicateWarning(null); handleConfirmPlanilla(i, { skipDupCheck: true }); }}
                  style={{ padding: '10px 18px', borderRadius: 12, border: 'none', background: 'linear-gradient(135deg,#f59e0b,#d97706)', color: 'white', cursor: 'pointer', fontWeight: 700, fontSize: 13, fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <Check style={{ width: 14, height: 14 }} /> Importar igual
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default AdminFlightLogImport;
